import React, { useState } from 'react';
import i18n from 'i18next';
import { AnimatePresence, motion } from 'framer-motion';
import Error from '@mui/icons-material/Error';

import { Form } from 'core/forms';
import { Button } from 'uikit/buttons';
import { Loading } from 'uikit/loaders';
import { Typo } from 'uikit/content';

import intellumLogo from '../assets/intellum-logo.png';

import styles from '../styles/experienceSSO.module.scss';

const ExperienceSSO = () => {
  const defaultDomain = i18n.t('domain');

  const [domain, setDomain] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const onUpdate = ({ update }) => {
    setDomain(update.domain.trim());
    setError('');
  };

  const onSubmit = async () => {
    if (!domain) return;
    setIsLoading(true);
    try {
      const trimmedDomain = domain.replace(/^(https?:\/\/)/, '');
      const response = await fetch('/api/experience/sso', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: trimmedDomain }),
      });
      const data = await response.json();
      window.location.assign(data.redirectUrl);
    } catch {
      setError(i18n.t('invalid-domain-error'));
    } finally {
      setIsLoading(false);
    }
  };

  const schema = {
    domain: {
      type: 'Text',
      label: i18n.t('domain'),
      help: i18n.t('login-page-prompt'),
      onKeyDown: (event) => {
        if (event.key === 'Enter' && domain) onSubmit();
      },
      autoComplete: 'off',
      autoCorrect: 'off',
      spellCheck: 'false',
      testId: 'domain-input',
    },
  };

  const domainText = domain.includes('.') ? domain : `${domain || defaultDomain}.exceedlms.com`;

  return (
    <div className={styles.element} data-testid="experience-sso">
      <div className={styles.form}>
        <div className={styles.formContent}>
          <div className={styles.header}>
            <Typo element="h1" variant="d05">
              {i18n.t('welcome-message')}
            </Typo>
          </div>
          <div className={styles.content}>
            <Form onUpdate={onUpdate} schema={schema} model={{ domain }} />
            <div className={styles.notice}>
              <AnimatePresence exitBeforeEnter>
                {error ? (
                  <motion.div
                    className={styles.error}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    key="error"
                    aria-live="assertive"
                    role="alert"
                    data-testid="error"
                  >
                    <Error sx={{ height: 16, width: 16 }} />
                    <Typo element="p" variant="s01" className={styles.errorText}>
                      {error}
                    </Typo>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    key="domain"
                    data-testid="domain"
                  >
                    <Typo color="secondary" element="p" variant="s01">
                      {domainText}
                    </Typo>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          <div className={styles.footer}>
            <Button
              color="brand"
              isDisabled={!domain || isLoading}
              className={styles.continue}
              onClick={onSubmit}
              testId="continue-button"
              ariaLabel={i18n.t('continue')}
            >
              <AnimatePresence exitBeforeEnter>
                {isLoading ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    key="loading"
                    data-testid="loading"
                  >
                    <Loading size={20} />
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    key="continue"
                    data-testid="continue"
                  >
                    <Typo element="span" variant="s03">
                      {i18n.t('continue')}
                    </Typo>
                  </motion.div>
                )}
              </AnimatePresence>
            </Button>
          </div>
        </div>
        <img
          src={intellumLogo}
          className={styles.logo}
          aria-label={i18n.t('intellum-logo-description')}
        />
      </div>
      <div className={styles.posterContainer}>
        <div className={styles.poster} aria-label={i18n.t('graphic-description')} role="img" />
      </div>
    </div>
  );
};

export default ExperienceSSO;
